import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core'
import { FormControl, FormGroup, UntypedFormGroup } from '@angular/forms'
import { MatButtonToggleModule } from '@angular/material/button-toggle'
import { MAT_DIALOG_DATA } from '@angular/material/dialog'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { LinkToSelectorComponent } from '@app/features/scope-overview/components/scope-tab/link-to-selector/link-to-selector.component'
import { FeeAmountType, FeeItem } from '@app/features/scope-overview/model/fee-item.model'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { ModalConfig } from '@core/model/modal-config.model'
import { ScopeUiDropdownComponent } from '@shared/components/ui-components/scope-ui-dropdown/scope-ui-dropdown.component'
import { ScopeUiInputComponent } from '@shared/components/ui-components/scope-ui-input/scope-ui-input.component'
import { SharedModule } from '@shared/shared.module'
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material'
import { Observable } from 'rxjs'

@Component({
  selector: 'add-edit-fee-modal',
  templateUrl: './add-edit-fee-modal.component.html',
  styleUrls: ['./add-edit-fee-modal.component.scss'],
  imports: [
    SharedModule,
    NgxDaterangepickerMd,
    ScopeUiInputComponent,
    ScopeUiDropdownComponent,
    MatButtonToggleModule,
    LinkToSelectorComponent,
  ],
  standalone: true,
})
export class AddEditFeeModalComponent implements OnInit {
  @Input() modalTitle: string
  @Input() existingFees$: Observable<FeeItem[]>
  @Input() feeItem: any
  @Input() currencyCode: string
  @Input() linkFee: boolean
  @Input() sections: ScopeSection[]
  @Input() deliverables: Deliverable[]
  @Input() components$: Observable<ScopeComponent[]>
  @Input() editPresetValues = false
  @Output() onSubmit: EventEmitter<any>
  @Output() onSearchComponents: EventEmitter<string>

  modalForm: UntypedFormGroup
  selection: FeeItem
  isNewFee = false
  isPresetEditMode = false
  typeControl = new FormControl('INCREASE')
  amountTypeControl = new FormControl(FeeAmountType.MONETARY)
  newFee: any
  level: { name: string; value: string }
  linkTo: any

  constructor(@Inject(MAT_DIALOG_DATA) public data: ModalConfig) {
    this.modalForm = new FormGroup({})
    this.onSubmit = new EventEmitter<any>()
    this.onSearchComponents = new EventEmitter<string>()
  }

  ngOnInit() {
    if (this.feeItem) {
      this.existingFees$.subscribe((existingFees) => {
        if (existingFees) {
          const selectedFee = existingFees.filter((f) => f.id == this.feeItem.id || f.id === this.feeItem.baseFeeId)[0]
          if (selectedFee) {
            this.isNewFee = false
            this.selectFee(selectedFee)
            if (this.editPresetValues) {
              this.isPresetEditMode = true
              this.initFeeControls(this.feeItem)
            } else {
              this.newFee = {
                name: '',
                description: '',
                amount: 0,
                chargeType: 'INCREASE',
                amountType: FeeAmountType.MONETARY,
              }
            }
          } else if (this.editPresetValues && this.feeItem.baseFeeId) {
            this.isNewFee = false
            this.isPresetEditMode = true
            this.selection = this.feeItem
            this.initFeeControls(this.feeItem)
          } else {
            this.isNewFee = true
            this.initFeeControls(this.feeItem)
          }
        }
      })
    } else {
      this.newFee = { name: '', description: '', amount: 0, chargeType: 'INCREASE', amountType: FeeAmountType.MONETARY }
    }
  }

  selectFee(fee: any) {
    this.selection = fee
  }

  private initFeeControls(fee: any) {
    this.newFee = {
      ...fee,
      amount: Math.abs(fee.amount),
      chargeType: fee.amount > 0 ? 'INCREASE' : 'DECREASE',
    }
    this.typeControl.setValue(this.newFee.chargeType)
    this.amountTypeControl.setValue(this.newFee.amountType)
  }

  confirm() {
    if (this.editPresetValues && !this.isNewFee && !this.isPresetEditMode) {
      this.isPresetEditMode = true
      return this.initFeeControls(this.selection)
    }

    if (this.isPresetEditMode) {
      const amount =
        this.typeControl.value === 'INCREASE' ? Math.abs(this.newFee.amount) : -Math.abs(this.newFee.amount)

      return this.onSubmit.emit({
        feeItem: {
          ...this.selection,
          amount,
          chargeType: this.typeControl.value,
          amountType: this.amountTypeControl.value,
        },
        element: this.linkTo,
      })
    }

    this.newFee.chargeType = this.typeControl.value
    this.newFee.amountType = this.amountTypeControl.value
    this.newFee.amount =
      this.newFee.chargeType === 'INCREASE' ? Math.abs(this.newFee.amount) : -Math.abs(this.newFee.amount)
    this.onSubmit.emit({ feeItem: this.isNewFee ? this.newFee : this.selection, element: this.linkTo })
  }

  protected readonly close = close
  protected readonly FeeAmountType = FeeAmountType
  protected readonly Privilege = Privilege

  isFormValid() {
    if (this.isPresetEditMode) {
      return (
        this.newFee.amount &&
        this.newFee.amount > 0.01 &&
        (!this.linkFee || this.level?.value == 'scope' || (this.level && this.linkTo))
      )
    }
    return (
      (this.isNewFee ? this.newFee.name && this.newFee.amount && this.newFee.amount > 0.01 : this.selection) &&
      (!this.linkFee || this.level?.value == 'scope' || (this.level && this.linkTo))
    )
  }
}
